import { Routes } from '@angular/router';
import { App } from './app';
import { LayoutComponent } from '@core/layout/layout.component';
import { MyProfileComponent } from './user/my-profile/my-profile.component';
import { AuthGuard } from '@core/security/auth.guard';
import { TableSettingsGuard } from '@core/services/table-settings-gaurd';
import { RecoverPasswordResolver } from './recover-password/recover-password-resolver';
import { CompanyProfileResolver } from './company-profile/company-profile-resolver';
import { salesOrderTaxResolver } from './sales-order/sales-order-add-edit/sales-order-tax-resolver';
import { salesOrderUnitResolver } from './sales-order/sales-order-add-edit/sales-order-unit-resolver';

export const routes: Routes = [
  {
    path: '',
    component: App,
    resolve: { profile: CompanyProfileResolver },
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./forgot-password/forgot-password.component')
            .then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password/:link',
        loadComponent: () =>
          import('./recover-password/recover-password.component')
            .then(m => m.RecoverPasswordComponent),
        resolve: {
          UserReset: RecoverPasswordResolver
        }
      },
      {
        path: '',
        component: LayoutComponent,
        children: [
          {
            path: '',
            data: {
              claimType: ['DB_STATISTICS',
                'DB_BEST_SELLING_PROS',
                'DB_RECENT_SO_SHIPMENT',
                'DB_RECENT_PO_DELIVERY',
                'DB_PROD_STOCK_ALERT']
            },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dashboard/dashboard.component').then(
                (m) => m.DashboardComponent
              ),
          }, {
            path: 'my-profile',
            component: MyProfileComponent,
            canActivate: [AuthGuard],
          },
          {
            path: 'pos',
            loadComponent: () => import('./pos/pos.component').then((m) => m.PosComponent),
            data: {
              claimType: ['POS_POS'],
            },
            canActivate: [AuthGuard],
            resolve: {
              units: salesOrderUnitResolver,
              taxs: salesOrderTaxResolver,
            },
          },
          {
            path: 'pages',
            canLoad: [AuthGuard],
            data: { claimType: 'dummay_permission' },
            loadComponent: () =>
              import('./page/page-list/page-list.component').then((m) => m.PageListComponent),
          },
          {
            path: 'roles',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./role/role-routes').then((m) => m.ROLE_ROUTES),
          },
          {
            path: 'users',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./user/user-routes').then((m) => m.USER_ROUTES),
          },
          {
            path: 'login-audit',
            data: { claimType: 'LOGS_VIEW_LOGIN_AUDITS' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./login-audit/login-audit-list/login-audit-list.component').then(
                (m) => m.LoginAuditListComponent
              ),
          },
          {
            path: 'emailtemplate',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./email-template/email-template-routes').then(
                (m) => m.EMAIL_TEMPLATE_ROUTES
              ),
          },
          {
            path: 'send-email',
            canActivate: [AuthGuard],
            data: { claimType: 'EMAIL_SEND_EMAIL' },
            loadComponent: () =>
              import('./email-send/email-send.component').then(
                (m) => m.EmailSendComponent
              ),
          },
          {
            path: 'email-smtp',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./email-smtp-setting/email-smtp-setting-routes').then(
                (m) => m.EMAIL_SMTP_SETTING_ROUTES
              ),
          },
          {
            path: 'supplier',
            loadChildren: () =>
              import('./supplier/supplier-routes').then(
                (m) => m.SUPPLIER_ROUTES
              ),
          },
          {
            path: 'customer',
            loadChildren: () =>
              import('./customer/customer-routing').then(
                (m) => m.CUSTOMER_ROUTES
              ),
          },
          {
            path: 'customer-cobros',
            loadChildren: () =>
              import('./customer-cobros/customer-cobros-routes').then(
                (m) => m.CUSTOMER_COBROS_ROUTES
              ),
          },
          {
            path: 'customer-transactions',
            loadChildren: () =>
              import('./customer-transactions/customer-transactions-routes').then(
                (m) => m.CUSTOMER_TRANSACTIONS_ROUTES
              ),
          },
          {
            path: 'supplier-invoices',
            loadChildren: () =>
              import('./supplier-invoices/supplier-invoices-routes').then(
                (m) => m.SUPPLIER_INVOICES_ROUTES
              ),
          },
          {
            path: 'supplier-invoice-discounts',
            loadChildren: () =>
              import('./supplier-invoice-discounts/supplier-invoice-discounts-routes').then(
                (m) => m.SUPPLIER_INVOICE_DISCOUNTS_ROUTES
              ),
          },
          {
            path: 'supplier-transactions',
            loadChildren: () =>
              import('./supplier-transactions/supplier-transactions-routes').then(
                (m) => m.SUPPLIER_TRANSACTIONS_ROUTES
              ),
          },
          {
            path: 'supplier-bank-drafts',
            loadChildren: () =>
              import('./supplier-bank-drafts/supplier-bank-drafts-routes').then(
                (m) => m.BANK_DRAFTS_ROUTES
              ),
          },
          {
            path: 'supplier-bank-transfers',
            loadChildren: () =>
              import('./supplier-bank-transfers/supplier-bank-transfers-routes').then(
                (m) => m.BANK_TRANSFERS_ROUTES
              ),
          },
          {
            path: 'supplier-direct-transfers',
            loadChildren: () =>
              import('./supplier-direct-transfers/supplier-invoices-routes').then(
                (m) => m.SUPPLIER_DIRECT_TRANSFERS_ROUTES
              ),
          },
          {
            path: 'supplier-expenses',
            loadChildren: () =>
              import('./supplier-expenses/supplier-expenses-routes').then(
                (m) => m.SUPPLIER_EXPENSES_ROUTES
              ),
          },
          {
            path: 'supplier-payments',
            loadChildren: () =>
              import('./supplier-payments/supplier-payments-routes').then(
                (m) => m.SUPPLIER_PAYMENTS_ROUTES
              ),
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./notification/notification.component').then(
                (m) => m.NotificationComponent
              ),
          },
          {
            path: 'reminders',
            loadChildren: () =>
              import('./reminder/reminder-routes').then(
                (m) => m.REMINDER_ROUTES
              ),
            canLoad: [AuthGuard],
          },
          {
            path: 'purchase',
            loadChildren: () =>
              import('./purchase/purchase-routes').then(
                (m) => m.PURCHASE_ORDER_ROUTES
              ),
          },
          {
            path: 'purchase-return',
            loadChildren: () =>
              import(
                './purchase-return/purchase-return-routes'
              ).then((m) => m.PURCHASE_ORDER_RETURNS_ROUTES),
          },
          {
            path: 'purchase-request',
            loadChildren: () =>
              import(
                './purchase-request/purchase-request-routes'
              ).then((m) => m.PURCHASE_ORDER_REQUEST_ROUTES),
          },
          {
            path: 'purchases-orders',
            loadChildren: () =>
              import('./purchases-orders/purchases-orders-routes').then(
                (m) => m.PURCHASES_ORDERS_ROUTES
              ),
          },
          {
            path: 'inventory-adjustment',
            loadChildren: () =>
              import('./inventory-adjustment/inventory-adjustment-routes').then(
                (m) => m.INVENTORY_ADJUSTMENT_ROUTES
              ),
          },
          {
            path: 'inventory-transfer',
            loadChildren: () =>
              import('./inventory-transfer/inventory-transfer-routes').then(
                (m) => m.INVENTORY_TRANSFER_ROUTES
              ),
          },
          {
            path: 'sales',
            loadChildren: () =>
              import('./sales/sales-routes').then(
                (m) => m.SALES_ROUTES
              ),
          },
          {
            path: 'pos-offline',
            loadChildren: () =>
              import('./pos-offline/pos-offline-routes').then(
                (m) => m.POS_OFFLINE_ROUTES
              ),
          },
          {
            path: 'pos-offline-reception',
            loadChildren: () =>
              import('./pos-offline-reception/pos-offline-reception-routes').then(
                (m) => m.POS_OFFLINE_RECEPTION_ROUTES
              ),
          },
          {
            path: 'sales-order',
            loadChildren: () =>
              import('./sales-order/sales-order-routes').then(
                (m) => m.SALE_RODER_ROUTES
              ),
          },
          {
            path: 'sales-order-return',
            loadChildren: () =>
              import('./sale-order-return/sale-order-return-routes').then(
                (m) => m.SALE_ORDER_RETURN_ROUTES
              ),
          },
          {
            path: 'sales-order-request',
            loadChildren: () =>
              import('./sales-order-request/sales-order-request-routes').then(
                (m) => m.SALES_ORDER_REQUEST_ROUTES
              ),
          },
          {
            path: 'company-profile',
            canLoad: [AuthGuard],
            data: { claimType: 'SETT_UPDATE_COM_PROFILE' },
            resolve: {
              profile: CompanyProfileResolver
            },
            loadComponent: () =>
              import('./company-profile/company-profile.component').then(
                (m) => m.CompanyProfileComponent
              ),
          },
          {
            path: 'expense-category',
            canActivate: [AuthGuard],
            data: { claimType: 'EXP_CATEGORY_VIEW' },
            loadComponent: () =>
              import('./expense-category/expense-category-list/expense-category-list.component').then(
                (m) => m.ExpenseCategoryListComponent
              ),
          },
          {
            path: 'expense',
            loadChildren: () =>
              import('./expense/expense-routes').then((m) => m.EXPENSE_ROUTES),
          },
          {
            path: 'inquiry',
            loadChildren: () =>
              import('./inquiry/inquiry-routes').then((m) => m.INQUIRY_ROUTES),
          },
          {
            path: 'inquiry-status',
            canActivate: [AuthGuard],
            data: { claimType: 'INQ_STATUS_VIEW' },
            loadComponent: () =>
              import('./inquiry-status/inquiry-status-list/inquiry-status-list.component').then(
                (m) => m.InquiryStatusListComponent
              ),
          },
          {
            path: 'inquiry-source',
            canActivate: [AuthGuard],
            data: { claimType: 'INQ_SOURCE_VIEW' },
            loadComponent: () =>
              import('./inquiry-source/inquiry-source-list/inquiry-source-list.component').then(
                (m) => m.InquirySourceListComponent
              ),
          },
          {
            path: 'product-category',
            canActivate: [AuthGuard],
            data: { claimType: 'PRODUCT_CATEGORY_VIEW' },
            loadComponent: () =>
              import('./product-category/product-category-list/product-category-list.component').then(
                (m) => m.ProductCategoryListComponent
              ),
          },
          {
            path: 'products',
            loadChildren: () =>
              import('./product/product-routes').then((m) => m.PRODUCT_ROUTES),
          },
          {
            path: 'print-labels',
            loadComponent: () => import('./barcode-generator/barcode-generator.component').then((m) => m.BarcodeGeneratorComponent),
            data: { claimType: 'PRO_PRINT_LABELS' },
            canActivate: [AuthGuard]
          },
          {
            path: 'variants',
            data: { claimType: 'PRODUCT_VARIANT_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./variants/variants-list/variants-list.component').then(
                (m) => m.VariantsListComponent
              ),
          },
          {
            path: 'tax',
            data: { claimType: 'PRODUCT_TAX_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./tax/tax-list/tax-list.component').then((m) => m.TaxListComponent),
          },
          {
            path: 'brand',
            data: { claimType: 'PRODUCT_BRAND_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./brand/brand-list/brand-list.component').then((m) => m.BrandListComponent),
          },
          {
            path: 'offer',
            data: { claimType: 'PRODUCT_OFFER_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./offer/offer-list/offer-list.component').then((m) => m.OfferListComponent),
          },
          {
            path: 'classification/1',
            data: { claimType: 'CLASSIFICATIONS_CLASS1_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-1/classification-1-list.component').then((m) => m.Classification1ListComponent),
          },
          {
            path: 'classification/2',
            data: { claimType: 'CLASSIFICATIONS_CLASS2_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-2/classification-2-list.component').then((m) => m.Classification2ListComponent),
          },
          {
            path: 'classification/3',
            data: { claimType: 'CLASSIFICATIONS_CLASS3_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-3/classification-3-list.component').then((m) => m.Classification3ListComponent),
          },
          {
            path: 'classification/4',
            data: { claimType: 'CLASSIFICATIONS_CLASS4_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-4/classification-4-list.component').then((m) => m.Classification4ListComponent),
          },
          {
            path: 'classification/5',
            data: { claimType: 'CLASSIFICATIONS_CLASS5_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-5/classification-5-list.component').then((m) => m.Classification5ListComponent),
          },
          {
            path: 'classification/6',
            data: { claimType: 'CLASSIFICATIONS_CLASS6_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-6/classification-6-list.component').then((m) => m.Classification6ListComponent),
          },
          {
            path: 'classification/7',
            data: { claimType: 'CLASSIFICATIONS_CLASS7_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-7/classification-7-list.component').then((m) => m.Classification7ListComponent),
          },
          {
            path: 'classification/8',
            data: { claimType: 'CLASSIFICATIONS_CLASS8_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-8/classification-8-list.component').then((m) => m.Classification8ListComponent),
          },
          {
            path: 'classification/9',
            data: { claimType: 'CLASSIFICATIONS_CLASS9_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-9/classification-9-list.component').then((m) => m.Classification9ListComponent),
          },
          {
            path: 'classification/10',
            data: { claimType: 'CLASSIFICATIONS_CLASS10_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./classification-10/classification-10-list.component').then((m) => m.Classification10ListComponent),
          },
          {
            path: 'arancel',
            data: { claimType: 'ARANCEL_VIEW' },
            canActivate: [AuthGuard],
            loadChildren: () =>
              import('./arancel/arancel-routes').then((m) => m.ARANCEL_ROUTES),
          },
          {
            path: 'dmce-companies-zl',
            data: { claimType: 'DMCE_COMPANIES_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dmce-companies-zl/dmce-companies-zl-list/dmce-companies-zl-list.component').then(
                (m) => m.DMCECompaniesZLListComponent
              ),
          },
          {
            path: 'dmce-countries-zl',
            data: { claimType: 'DMCE_COUNTRIES_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dmce-countries-zl/dmce-countries-zl-list/dmce-countries-zl-list.component').then(
                (m) => m.DMCECountriesZLListComponent
              ),
          },
          {
            path: 'dmce-origin-ports-zl',
            data: { claimType: 'DMCE_ORIGIN_PORTS_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dmce-origin-ports-zl/dmce-origin-ports-zl-list/dmce-origin-ports-zl-list.component').then(
                (m) => m.DMCEOriginPortsZLListComponent
              ),
          },
          {
            path: 'dmce-entry-ports-zl',
            data: { claimType: 'DMCE_ENTRY_PORTS_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dmce-entry-ports-zl/dmce-entry-ports-zl-list/dmce-entry-ports-zl-list.component').then(
                (m) => m.DMCEEntryPortsZLListComponent
              ),
          },
          {
            path: 'dmce-carriers-zl',
            data: { claimType: 'DMCE_CARRIERS_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./dmce-carriers-zl/dmce-carriers-zl-list/dmce-carriers-zl-list.component').then(
                (m) => m.DMCECarriersZLListComponent
              ),
          },
          {
            path: 'dmce-transports',
            data: { claimType: 'DMCE_TRANSPORTS_VIEW' },
            canActivate: [AuthGuard],
            loadChildren: () =>
              import('./dmce-transports/dmce-transports-routes').then(
                (m) => m.DMCE_TRANSPORTS_ROUTES
              ),
          },
          {
            path: 'warehouses',
            data: { claimType: 'WAREHOUSE_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./warehouses/warehouses-list/warehouses-list.component').then((m) => m.WarehousesListComponent),
          },
          {
            path: 'reference-movements',
            data: { claimType: 'PRODUCT_VIEW_REFERENCE_MOVEMENTS' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./reference-movements/reference-movements.component').then(
                (m) => m.ReferenceMovementsComponent
              ),
          },
          {
            path: 'country',
            data: { claimType: 'SETT_COUNTRY_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./country/country-list/country-list.component').then((m) => m.CountryListComponent),
          },
          {
            path: 'cities',
            data: { claimType: 'SETT_CITY_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./city/city-list/city-list.component').then((m) => m.CityListComponent),
          },
          {
            path: 'inventory',
            data: { claimType: 'INVE_VIEW_INVENTORIES' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./inventory/inventory-list/inventory-list.component').then(
                (m) => m.InventoryListComponent
              ),
          },
          {
            path: 'purchase-report',
            data: { claimType: 'REP_PO_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/purchase-report/purchase-report.component'
              ).then((m) => m.PurchaseReportComponent),
          },
          {
            path: 'sales-order-report',
            data: { claimType: 'REP_SO_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/sales-order-report/sales-order-report.component'
              ).then((m) => m.SalesOrderReportComponent),
          },
          {
            path: 'purchase-payment-report',
            data: { claimType: 'REP_PO_PAYMENT_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/purchase-payment-report/purchase-payment-report.component'
              ).then((m) => m.PurchasePaymentReportComponent),
          },
          {
            path: 'sales-payment-report',
            data: { claimType: 'REP_SO_PAYMENT_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/sales-payment-report/sales-payment-report.component'
              ).then((m) => m.SalesPaymentReportComponent),
          },
          {
            path: 'sales-purchase-report',
            data: { claimType: 'REP_SALES_VS_PURCHASE_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/sales-purchase-report/sales-purchase-report.component'
              ).then((m) => m.SalesPurchaseReportComponent),
          },
          {
            path: 'expense-report',
            data: { claimType: 'REP_EXPENSE_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./reports/expense-report/expense-report.component').then(
                (m) => m.ExpenseReportComponent
              ),
          },
          {
            path: 'supplier-payment-report',
            data: { claimType: 'REP_SUP_PAYMENT_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/supplier-payments/supplier-payments.component'
              ).then((m) => m.SupplierPaymentsComponent),
          },
          {
            path: 'customer-payment-report',
            data: { claimType: 'REP_CUST_PAYMENT_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/customer-payment-report/customer-payment-report.component'
              ).then((m) => m.CustomerPaymentReportComponent),
          },
          {
            path: 'product-purchase-report',
            data: { claimType: 'REP_PRO_PP_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/product-purchase-report/product-purchase-report.component'
              ).then((m) => m.ProductPurchaseReportComponent),
          },
          {
            path: 'product-sales-report',
            data: { claimType: 'REP_PRO_SO_REPORT' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/product-sales-report/product-sales-report.component'
              ).then((m) => m.ProductSalesReportComponent),
          },
          {
            path: 'stock-report',
            data: { claimType: 'REP_STOCK_REPORT' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./reports/stock-report/stock-report.component').then(
                (m) => m.StockReportComponent
              ),
          },
          {
            path: 'profit-loss-report',
            data: { claimType: 'REP_VIEW_PRO_LOSS_REP' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './reports/profit-loss-report/profit-loss-report.component'
              ).then((m) => m.ProfitLossReportComponent),
          },
          {
            path: 'reports/input-tax-report',
            loadComponent: () => import('./reports/tax-report/input-tax-report/input-tax-report.component').then((m) => m.InputTaxReportComponent),
            data: { claimType: 'REP_VIEW_INPUT_TAX_REP' },
            canActivate: [AuthGuard]
          },
          {
            path: 'reports/out-tax-report',
            loadComponent: () => import('./reports/tax-report/out-tax-report/out-tax-report.component').then((m) => m.OutTaxReportComponent),
            data: { claimType: 'REP_VIEW_OUTPUT_TAX_REP' },
            canActivate: [AuthGuard]
          }, {
            path: 'reports/expense-tax-report',
            loadComponent: () => import('./reports/tax-report/expense-tax-report/expense-tax-report.component').then((m) => m.ExpenseTaxReportComponent),
            data: { claimType: 'REP_VIEW_EXPENSE_TAX_REP' },
            canActivate: [AuthGuard]
          },
          {
            path: 'unitConversation',
            canActivate: [AuthGuard],
            data: { claimType: 'PRODUCT_UNIT_VIEW' },
            loadComponent: () =>
              import('./unit-conversation/unit-conversation-list/unit-conversation-list.component').then(
                (m) => m.UnitConversationListComponent
              ),
          },
          {
            path: 'languages',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./languages/languages.routes').then(
                (m) => m.LANGUAGES_ROUTES
              ),
          }, {
            path: 'stock-transfer',
            loadChildren: () =>
              import('./stock-transfer/stock-transfer-routes').then(
                (m) => m.STOCK_TRANSFER_ROUTES
              ),
          },
          {
            path: 'damaged-stock',
            loadChildren: () =>
              import('./damaged-stock/damaged-stock-routes').then(
                (m) => m.DAMAGED_STOCK_ROUTES
              ),
          },
          {
            path: 'locations',
            data: { claimType: 'SETT_LOC_VIEW' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./business-location/business-location-list/business-location-list.component').then(
                (m) => m.BusinessLocationListComponent
              ),
          },
          {
            path: 'page-helper',
            loadChildren: () =>
              import('./page-helper/page-helper-routes').then(
                (c) => c.PAGE_HEPLPER_ROUTES
              ),
          },
          {
            path: 'email-logs',
            loadComponent: () => import('./email-logs/email-logs.component').then((m) => m.EmailLogsComponent),
            data: { claimType: 'LOGS_VIEW_EMAIL_LOGS' },
          },
          {
            path: 'logs',
            loadChildren: () => import('./n-log/n-log-routes').then((m) => m.NLOG_ROUTES),
            data: { claimType: 'LOGS_VIEW_ERROR_LOGS' },
          },
          {
            path: 'table-settings/:screenName',
            canActivate: [TableSettingsGuard],
            loadComponent: () => import('./table-setting/table-setting.component').then((m) => m.TableSettingComponent)
          },
          {
            path: 'accounting',
            loadChildren: () =>
              import('./accounting/accounting-routes').then(
                (m) => m.ACCOUNTING_ROUTES
              ),
          },
          {
            path: 'book-close',
            data: { claimType: 'ACCOUNTING_VIEW_BOOK_CLOSE' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./accounting/book-close/book-close').then(
                (m) => m.BookClose
              ),
          },
          {
            path: 'customer-ladger',
            loadChildren: () =>
              import('./customer-ladger/customer-ladger-routing.module').then(
                (m) => m.CustomerLadgerRoutingModule
              ),
          },
          {
            path: 'customer-sales-order',
            data: { claimType: 'CUST_VIEW_CUSTOMER_PENDING_PAYMENTS' },
            canActivate: [AuthGuard],
            loadComponent: () =>
              import(
                './customer-sales-order/customer-sales-order-list/customer-sales-order-list.component'
              ).then((m) => m.CustomerSalesOrderListComponent),
          },
          {
            path: 'pay-roll',
            loadChildren: () =>
              import('./pay-roll/pay-roll-routing.module').then(
                (m) => m.PayRollRoutingModule
              ),
          },
          {
            path: 'rrhh',
            loadChildren: () =>
              import('./rrhh/rrhh-routes').then(
                (m) => m.RRHH_ROUTES
              ),
          },
          {
            path: 'calendar-view',
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./calendar-view/calendar-view').then(
                (m) => m.CalendarView
              )
          },
          {
            path: '**',
            redirectTo: '/',
          },
        ],
      },
    ],
  },
];



